import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { isSubActive } from "@/utils/subscription";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 10;

// Повернення зі Stripe Checkout. Webhook — джерело правди; ця сторінка лише поллить
// /auth/me, доки subscription не стане active/trialing. session_id читаємо тільки в лог.
export default function BillingSuccessPage() {
  const { api, updateUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | active | pending

  // api()/updateUser не мемоізовані у контексті — тримаємо у ref, щоб poll лишався стабільним
  const apiRef = useRef(api);
  const updateUserRef = useRef(updateUser);
  apiRef.current = api;
  updateUserRef.current = updateUser;

  const cancelledRef = useRef(false);
  const runningRef = useRef(false);

  const poll = useCallback(async () => {
    if (runningRef.current) return; // не запускати другий цикл поверх активного
    runningRef.current = true;
    cancelledRef.current = false;
    setStatus("checking");
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      if (cancelledRef.current) break;
      try {
        const res = await apiRef.current().get("/auth/me");
        updateUserRef.current(res.data); // оновлюємо контекст (знімає paywall/корони по всьому застосунку)
        if (isSubActive(res.data)) {     // перевірка на СВІЖОМУ res.data, не на застарілому user із замикання
          runningRef.current = false;
          if (!cancelledRef.current) setStatus("active");
          return;
        }
      } catch (e) {
        /* transient (webhook міг ще не обробитись / мережа) — просто пробуємо ще */
      }
      if (attempt < MAX_ATTEMPTS && !cancelledRef.current) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      }
    }
    runningRef.current = false;
    if (!cancelledRef.current) setStatus("pending"); // не встигло — НЕ помилка, показуємо «оновлюється»
  }, []);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    console.log("[billing] success return, session_id:", sessionId);
    poll();
    return () => {
      cancelledRef.current = true;
    };
  }, [poll]);

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl p-8 border border-black/5 text-center">
        {status === "checking" && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-[#7B9E7F]/10 flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-[#7B9E7F] animate-spin" />
            </div>
            <p className="text-black/60">{t("billing.success.checking")}</p>
          </>
        )}

        {status === "active" && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-[#7B9E7F]/10 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-[#7B9E7F]" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-bold mb-1">{t("billing.success.title")}</h1>
            <p className="text-black/60 mb-6">{t("billing.success.subtitle")}</p>
            <button
              onClick={() => navigate("/")}
              className="w-full bg-[#7B9E7F] text-white font-semibold py-4 rounded-2xl"
            >
              {t("billing.success.cta")}
            </button>
          </>
        )}

        {status === "pending" && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-[#7B9E7F]/10 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-[#7B9E7F]" strokeWidth={2.5} />
            </div>
            <p className="text-black/60 mb-6">{t("billing.success.pending")}</p>
            <button
              onClick={poll}
              className="w-full bg-[#7B9E7F] text-white font-semibold py-4 rounded-2xl"
            >
              {t("billing.success.refresh")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
